import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Phone, Star, Truck, Search } from 'lucide-react';
import apiService from '../../apiService';
import { useLiveRefresh } from '../../liveRefresh';
import { useAdmin } from '../../context/AdminContext';
import CityFilter from '../../components/CityFilter';

export default function DeliveryDriversPage() {
  const { setSelectedDriver } = useAdmin();
  const [drivers, setDrivers] = useState([]);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [cityFilter, setCityFilter] = useState(''); // '' = toutes les villes
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchDrivers = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiService.get(`/admin/delivery/drivers?filter=${filter}${cityFilter ? `&city_slug=${encodeURIComponent(cityFilter)}` : ''}`, { userType: 'admin' });
      if (response.success) setDrivers(response.data || []);
    } catch (err) {
      setError('Impossible de charger les livreurs');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchDrivers(); }, [filter, cityFilter]);
  useLiveRefresh(() => fetchDrivers(), ['driver_*']);

  // Ouvre la modale « validation chauffeur » partagée (respecte les rôles de l'admin connecté).
  const openDriverModal = async (driverId) => {
    if (!driverId) return;
    try {
      const res = await apiService.get(`/admin/drivers/${driverId}/details`, { userType: 'admin' });
      if (res.success && res.driver) setSelectedDriver(res.driver);
      else setError('Impossible d\'ouvrir la fiche du livreur');
    } catch { setError('Impossible d\'ouvrir la fiche du livreur'); }
  };

  const handleValidate = async (driverId, approved) => {
    try {
      await apiService.patch(`/admin/delivery/drivers/${driverId}/validate`, { approved }, { userType: 'admin' });
      fetchDrivers();
    } catch { setError('Erreur lors de la validation'); }
  };

  if (loading) return <div style={{display:'flex',justifyContent:'center',alignItems:'center',height:'200px'}}><span style={{fontSize:'1.1rem',color:'#9AA0A8'}}>⏳ Chargement...</span></div>;

  // Recherche client (nom, téléphone, véhicule). L'ordre (plus récents d'abord) vient du backend.
  const q = search.trim().toLowerCase();
  const filteredDrivers = q
    ? drivers.filter(d => [d.full_name, d.phone, d.vehicle_type].some(v => v?.toLowerCase().includes(q)))
    : drivers;

  return (
    <div>
      <div style={{marginBottom:'1.5rem'}}>
        <h2 style={{margin:0,fontSize:'1.5rem',fontWeight:'700',color:'#1E2229'}}>🚚 Livreurs</h2>
        <p style={{margin:'0.25rem 0 0',fontSize:'0.875rem',color:'#9AA0A8'}}>{filteredDrivers.length} livreur(s){q ? ' trouvé(s)' : ' inscrits'}</p>
      </div>

      {error && <div style={{background:'#fef2f2',color:'#dc2626',padding:'0.75rem 1rem',borderRadius:'8px',marginBottom:'1rem',fontSize:'0.875rem'}}>{error}</div>}

      {/* Barre de recherche */}
      <div style={{position:'relative',marginBottom:'1rem'}}>
        <Search size={16} color="#9AA0A8" style={{position:'absolute',left:'0.85rem',top:'50%',transform:'translateY(-50%)'}} />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Rechercher un livreur (nom, téléphone, véhicule)…"
          style={{
            width:'100%', boxSizing:'border-box', padding:'0.6rem 2.25rem',
            borderRadius:'12px', border:'1px solid #E9EBEF', fontSize:'0.875rem',
            color:'#1E2229', outline:'none', background:'white',
          }}
        />
        {search && (
          <button onClick={() => setSearch('')} style={{
            position:'absolute', right:'0.6rem', top:'50%', transform:'translateY(-50%)',
            border:'none', background:'transparent', cursor:'pointer', color:'#9AA0A8', fontSize:'1rem', lineHeight:1,
          }}>✕</button>
        )}
      </div>

      <div style={{display:'flex',gap:'0.5rem',marginBottom:'1.25rem',flexWrap:'wrap',alignItems:'center'}}>
        <CityFilter value={cityFilter} onChange={setCityFilter} className="" style={{ background: 'white', border: '1px solid #E9EBEF', borderRadius: '12px', padding: '0.5rem 0.75rem', fontSize: '0.875rem', cursor: 'pointer', outline: 'none' }} />
        {[{key:'all',label:'Tous'},{key:'pending',label:'En attente'},{key:'validated',label:'Validés'}].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} style={{
            padding:'0.5rem 1rem', borderRadius:'12px', border: filter===f.key ? 'none' : '1px solid #E9EBEF',
            background: filter===f.key ? '#FFB900' : 'white', color: filter===f.key ? 'white' : '#5C636E',
            fontWeight:'600', fontSize:'0.875rem', cursor:'pointer'
          }}>{f.label}</button>
        ))}
      </div>

      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(350px, 1fr))',gap:'1rem'}}>
        {filteredDrivers.map(driver => (
          <div key={driver.id} style={{background:'white',borderRadius:'16px',padding:'1.25rem',boxShadow:'0 1px 3px rgba(0,0,0,0.08)'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:'0.75rem'}}>
              <div
                onClick={() => openDriverModal(driver.driver_id)}
                style={{display:'flex',alignItems:'center',gap:'0.75rem',cursor:'pointer'}}
                title="Voir la fiche"
              >
                <div style={{width:'48px',height:'48px',borderRadius:'50%',background:'#FEE9E2',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'1.2rem',fontWeight:'700',color:'#F54119'}}>
                  {driver.full_name?.charAt(0) || '?'}
                </div>
                <div>
                  <h3 style={{margin:0,fontWeight:'600',fontSize:'1rem',color:'#C9350F',textDecoration:'underline'}}>{driver.full_name}</h3>
                  <p style={{margin:0,fontSize:'0.875rem',color:'#9AA0A8',display:'flex',alignItems:'center',gap:'0.25rem'}}><Phone size={12}/> {driver.phone}</p>
                </div>
              </div>
              <span style={{
                padding:'0.25rem 0.5rem', borderRadius:'12px', fontSize:'0.75rem', fontWeight:'600',
                background: driver.is_validated ? '#ECFDF5' : '#fef9c3', color: driver.is_validated ? '#10B981' : '#ca8a04'
              }}>{driver.is_validated ? 'Validé' : 'En attente'}</span>
            </div>

            <div style={{display:'flex',alignItems:'center',gap:'1rem',fontSize:'0.875rem',color:'#5C636E',marginBottom:'0.75rem'}}>
              <span style={{display:'flex',alignItems:'center',gap:'0.25rem'}}><Truck size={14}/> {driver.vehicle_type || 'Moto'}</span>
              <span style={{display:'flex',alignItems:'center',gap:'0.25rem'}}><Star size={14} color="#eab308"/> {driver.rating || '—'}</span>
              <span>{driver.total_deliveries || 0} livraisons</span>
            </div>

            {!driver.is_validated && (
              <div style={{display:'flex',gap:'0.5rem'}}>
                <button onClick={() => handleValidate(driver.id, false)} style={{
                  flex:1,padding:'0.5rem',borderRadius:'12px',border:'2px solid #fecaca',background:'white',color:'#DC2626',
                  fontWeight:'600',fontSize:'0.875rem',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:'0.35rem'
                }}><XCircle size={16}/> Refuser</button>
                <button onClick={() => handleValidate(driver.id, true)} style={{
                  flex:1,padding:'0.5rem',borderRadius:'12px',border:'none',background:'#10B981',color:'white',
                  fontWeight:'600',fontSize:'0.875rem',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:'0.35rem'
                }}><CheckCircle size={16}/> Valider</button>
              </div>
            )}
          </div>
        ))}
      </div>

      {filteredDrivers.length === 0 && !loading && (
        <div style={{textAlign:'center',padding:'3rem',color:'#9AA0A8'}}>
          <p style={{fontSize:'2rem',marginBottom:'0.5rem'}}>🚚</p>
          <p>{q ? `Aucun livreur pour « ${search} »` : 'Aucun livreur trouvé'}</p>
        </div>
      )}
    </div>
  );
}
